<template>
  <div class="aso-designer-step">
    <v-input
      ref="title"
      v-model="model.title"
      :rules="modelValidations.title"
      class="fg-input-name"
      label="Название задачи"
      :disabled="disableNonMoneyFields"
      name="title"
      @validated="handleValidate"/>

    <div class="fg-input-description">
      <v-text-editor
        ref="description"
        v-model="model.description"
        :rules="modelValidations.description"
        :init-mode="disableNonMoneyFields ? 'view' : 'editor'"
        label="Описание задачи"
        name="description"
        @validated="handleValidate"/>
    </div>

    <div class="priority-block">
      <div class="label">
        Приоритет задачи:
      </div>
      <div class="priority-radio-btns">
        <el-radio v-model="model.priority" :disabled="disableNonMoneyFields" :label="0">
          Обычный
        </el-radio>
        <el-radio v-model="model.priority" :disabled="disableNonMoneyFields" :label="1">
          <i class="fa fa-angle-up"/>-
          Средний
        </el-radio>
        <el-radio v-model="model.priority" :disabled="disableNonMoneyFields" :label="2">
          <i
            class="fa fa-angle-double-up"/>- Высокий
        </el-radio>
      </div>
    </div>

    <div class="task-internal">
      <label>Выдать задачу:</label>
      <div class="options">
        <el-radio v-model="model.is_internal" label="1" :disabled="disableNonMoneyFields">
          Внутреннему сотруднику
        </el-radio>
        <el-radio v-model="model.is_internal" label="0" :disabled="disableNonMoneyFields">
          Фрилансеру
        </el-radio>
      </div>
      <div v-if="isFreelancer" class="task-cost">
        <el-tooltip
          effect="light"
          :placement="activeRoleName === 'aso_manager' ? 'top-start' : 'top'"
          :disabled="disableMoneyFields">
          <template #content>
            <cost-limit-tooltip :value="activeRoleName === 'aso_manager' ? updateCostLimit : publishCostLimit"/>
          </template>
          <v-input
            ref="cost"
            v-model="model.cost"
            :rules="modelValidations.cost"
            type="number"
            label="Стоимость задачи"
            :disabled="disableMoneyFields"
            step="100"
            placeholder=""
            transform-method="onlyDigits"
            name="cost"
            @validated="handleValidate"/>
        </el-tooltip>
      </div>
      <div v-if="isFreelancer" class="country-task">
        <v-select
          ref="task_country_show"
          v-model="model.task_country_show"
          :rules="modelValidations.task_country_show"
          label="Показать задание пользователям стран"
          :input-settings="inputSettings"
          multiple
          :disabled="disableNonMoneyFields"
          name="task_country_show"
          :options="types.countries"
          :option-label-getter="country => `${country.title} (${country.name})`"
          @clear="selectAllCountries = false"
          @remove-tag="selectAllCountries = false"
          @validated="handleValidate"/>

        <div class="checkbox-col country-checkbox">
          <p-checkbox
            v-model="selectAllCountries"
            class="select-all-checkbox"
            :disabled="disableNonMoneyFields">
            Выбрать все страны
          </p-checkbox>
        </div>
      </div>
    </div>

    <div v-if="!appStore" class="task-img-resolution">
      <v-select
        v-for="(resolution, index) in RESOLUTIONS_IMG_FIELDS"
        :key="index"
        :ref="`${resolution.fieldModel}`"
        v-model="model.settings[resolution.fieldModel]"
        :rules="modelValidations[resolution.fieldModel]"
        :label="resolution.title"
        :input-settings="inputSettingsResolution"
        :name="resolution.fieldModel"
        class="resolution-block"
        :options="filterResolutions(resolution.fieldModel)"
        @validated="handleValidate"/>
    </div>

    <div v-if="getShowInput('task_time')" class="task-time-block">
      <v-time-period
        ref="task_time"
        v-model="model.task_time"
        name="task_time"
        label="Время на выполнение задачи (максимум 20 дней)"
        :disabled="disableNonMoneyFields"
        :rules="modelValidations.task_time"
        @validated="handleValidate"/>
    </div>

    <files-uploader
      class="additional-files_uploader"
      :mode="disableNonMoneyFields ? 'list' : 'uploader'"
      :files="initFiles"
      :available-types="availableTypes"
      :size-limit="15"
      big-pictures
      show-label
      :can-download="taskType === 'update_app'"
      :handler="handleFileListChange">
      <template #label>
        <p class="label">
          Файлы и изображения для дизайнера
        </p>
      </template>
      <template #trigger>
        Нажмите здесь или перетащите сюда файлы, чтобы прикрепить их к задаче
      </template>
    </files-uploader>
  </div>
</template>

<script>
import FilesUploader from '@/components/UIComponents/Custom/FilesUploader'
import { ElRadio, ElTooltip } from 'element-plus'
import { mapGetters } from 'vuex'
import CostLimitTooltip from '@/components/UIComponents/Tooltip/CostLimitTooltip'
import VInput from '@/components/UIComponents/Custom/VInput'
import VTextEditor from '@/components/UIComponents/Custom/VTextEditor'
import VSelect from '@/components/UIComponents/Custom/VSelect'
import VTimePeriod from '@/components/UIComponents/Custom/VTimePeriod'
import { RESOLUTIONS_IMG_FIELDS } from '@/utils/constants'
import imgResolutions from '@/dictionary/imgResolutions.json'

export default {
  name: 'DesignerStep',
  components: {
    CostLimitTooltip,
    FilesUploader,
    ElRadio,
    ElTooltip,
    VInput,
    VTextEditor,
    VTimePeriod,
    VSelect,
  },
  props: {
    taskType: String,
    provisionalTitle: String,
    hideInputs: {
      type: Array,
      default: () => []
    },
    filled: Boolean,
    disableMoneyFields: {
      type: Boolean,
      default: false
    },
    disableNonMoneyFields: {
      type: Boolean,
      default: false
    },
    appStore: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      model: {
        title: '',
        priority: 0,
        description: '',
        task_files: [],
        is_internal: '1',
        cost: undefined,
        task_country_show: undefined,
        task_time: 3600,
        settings: {
          phone_screen_resolution: '960x1920',
          tablet_screen_resolution: '1080x1920'
        }
      },
      modelValidations: {
        title: {
          required: true,
          max: 150
        },
        description: {
          required: true
        },
        cost: undefined,
        task_country_show: undefined,
        task_time: {
          required: true,
          min_value: 3600,
          max_value: 1728000
        },
        phone_screen_resolution: {
          required: true,
        },
        tablet_screen_resolution: {
          required: true,
        }
      },
      initFiles: [],
      selectAllCountries: false,
      validChecked: false,
      taskFilesChanged: false,
      role: '',
      availableTypes: ['JPG', 'JPEG', 'PNG', 'ZIP', 'RAR', 'DOC', 'DOCX'],
      valid: {
        title: undefined,
        description: undefined,
        phone_screen_resolution: undefined,
        tablet_screen_resolution: undefined,
        cost: undefined,
        task_country_show: undefined,
        task_time: true
      },
      RESOLUTIONS_IMG_FIELDS: RESOLUTIONS_IMG_FIELDS
    }
  },
  computed: {
    ...mapGetters([
      'types',
      'selectedTask',
      'selectedMainTask',
      'activeRoleName',
      'updateTasksCostLimits',
      'publishTasksCostLimits'
    ]),
    isFreelancer() {
      return this.model.is_internal === '0'
    },
    collapseCountryTags() {
      return !!this.model.task_country_show && this.model.task_country_show.length > 10
    },
    invalidDescription() {
      return this.valid.description
    },
    filteredModel() {
      const result = { ...this.model }
      for (const field of this.hideInputs) {
        if (field === 'internal') {
          delete result.is_internal
          delete result.cost
          delete result.task_country_show
          continue
        }
        if (!Object.keys(this.model)
          .includes(field)) continue
        delete result[field]
      }
      if (this.model.is_internal === '1') {
        delete result.cost
        delete result.task_country_show
      }
      return result
    },
    showTaskInternal() {
      return !this.filled || this.isFreelancer
    },
    taskCountryChanged() {
      if (this.model.is_internal === '1') return false
      if (this.userTypeChanged) return true
      if (!this.selectedTask.task_country_show) return
      const countryIdNumber = this.selectedTask.task_country_show.map(item => Number(item))
      return !![
        ..._.difference(countryIdNumber, this.model.task_country_show),
        ..._.difference(this.model.task_country_show, countryIdNumber)
      ].length
    },
    titleChanged() {
      return this.model.title !== this.selectedTask.title
    },
    priorityChanged() {
      return this.model.priority !== this.selectedTask.priority
    },
    descriptionChanged() {
      return this.model.description !== this.selectedTask.description
    },
    taskTimeChanged() {
      return this.model.task_time !== this.selectedTask.task_time
    },
    costChanged() {
      if (this.model.is_internal === '1') return false
      if (this.userTypeChanged) return true
      return parseInt(this.selectedTask.cost) !== parseInt(this.model.cost)
    },
    userTypeChanged() {
      const userType = this.selectedTask.is_internal ? '1' : '0'
      return this.model.is_internal !== userType
    },
    changedData() {
      if (!this.filled) return
      return [
        this.titleChanged,
        this.priorityChanged,
        this.descriptionChanged,
        this.taskTimeChanged,
        this.costChanged,
        this.currencyChanged,
        this.taskCountryChanged,
        this.taskFilesChanged,
        this.userTypeChanged
      ].some(item => item)
    },
    publishCostLimit() {
      return Number(this.publishTasksCostLimits.find(item => item.name === 'cost_limit_create_designer_rub').value)
    },
    updateCostLimit() {
      return Number(this.updateTasksCostLimits.find(item => item.name === 'cost_limit_update_designer_rub').value)
    },
    inputSettings() {
      return {
        filterable: true,
        clearable: true,
        collapseTags: this.collapseCountryTags,
        placeholder: 'Начните печатать для поиска по списку',
        noMatchText: 'Нет совпадений'
      }
    },
    inputSettingsResolution() {
      return {
        ...this.inputSettings,
        placeholder: ' ',
      }
    },
    formIsValid() {
      return Object.values(this.valid).every(i => i !== false)
    }
  },
  watch: {
    isFreelancer(value) {
      if (this.filled && this.disableMoneyFields) return
      if (value) {
        if (!this.filled || (!this.model.cost && !this.model.task_country_show)) {
          this.model.cost = ''
          this.model.task_country_show = []
        }
        this.modelValidations.cost = {
          required: true,
          min_value: 1,
          max_value: this.activeRoleName === 'aso_manager' ? this.updateCostLimit : this.publishCostLimit
        }
        this.modelValidations.task_country_show = {
          required: true
        }
      } else {
        this.modelValidations.cost = undefined
        this.model.cost = undefined
        this.model.task_country_show = undefined
        this.selectAllCountries = false
      }
    },
    provisionalTitle(value) {
      this.model.title = value
    },
    selectAllCountries(value) {
      if (value) {
        this.model.task_country_show = this.types.countries.map(item => item.id)
      }
    },
    changedData(value) {
      const dataStatus = !this.formIsValid ? 'error' : 'success'
      this.$emit('data-changed', value ? dataStatus : value)
    },
    formIsValid(value) {
      if (!this.changedData) return
      this.$emit('data-changed', value ? 'success' : 'error')
      if (!this.filled) this.$emit('on-fields-filled', value)
    },
    appStore(value) {
      this.model.settings = {
        phone_screen_resolution: '',
        tablet_screen_resolution: ''
      }
      if(value) {
        this.model.description = '.'
      }
    }
  },
  methods: {
    async validate() {
      this.validChecked = true
      const correctTaskTime = !!this.model.task_time
      if (!correctTaskTime) {
        this.$notifyError('Необходимо указать время на выполнение задания')
      }
      await Promise.allSettled(Object.keys(this.valid).map(key => {
        const content = Array.isArray(this.$refs[key]) ? this.$refs[key][0] : this.$refs[key]
        return content?.trigger()
      }))
      const resultValid = this.formIsValid && correctTaskTime
      this.$emit('on-validated', resultValid, this.filteredModel)
      return resultValid
    },
    handleFileListChange(fileList) {
      this.model.task_files = fileList.map(i => i.url)
      if (!fileList.length && !this.initFiles.length) {
        this.taskFilesChanged = false
      } else if (!this.taskFilesChanged) {
        this.taskFilesChanged = true
      }
    },
    getShowInput(name) {
      return !this.hideInputs.includes(name)
    },
    getModel() {
      if (this.changedData) {
        return this.model
      }
    },
    handleValidate({ name, valid }) {
      this.valid[name] = valid
    },
    filterResolutions(typeResolution) {
      return imgResolutions.find(resolution => resolution.type === typeResolution).list_resolutions
    },
  },
  beforeMount() {
    if (this.filled) {
      for (const key of Object.keys(this.model)) {
        if (key === 'task_files') {
          let files = []
          const type = this.selectedTask.type
          for (const item of this.selectedMainTask.files.filter(item => item.type === type)) {
            files = files.concat(item.files)
          }
          this.initFiles = files
          this.model.task_files = files
          continue
        }
        if (key === 'is_internal') {
          this.model[key] = this.selectedTask[key] ? '1' : '0'
          continue
        }
        if (key === 'task_country_show') {
          this.model[key] = []
          if (!this.selectedTask[key]) continue
          for (const id of this.selectedTask[key]) {
            this.model[key].push(Number(id))
          }
          continue
        }
        this.model[key] = this.selectedTask[key]
      }
    }

    if (this.selectedTask && this.selectedTask.is_internal) {
      this.model.cost = ''
      this.model.task_country_show = []
    }
  }
}
</script>

<style>
</style>
